//Website builder.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, updateDoc } from 'firebase/firestore';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../services/firebase';
import {
  COMPONENTS,
  DEFAULT_THEME,
  TEMPLATE_CATEGORIES,
  createInitialContent,
  getTemplateById,
  getTemplatesByCategory,
  getThemeById,
} from '../features/websites/templates';
import '../styles/builder.css';
import { uploadImage } from '../services/cloudinary';
import { verifyCode } from '../services/verificationService';
import { verifyPromoCode, markPromoCodeAsUsed } from '../services/promoCodeService';
import { STRIPE_CONFIG } from '../config/stripeConfig';
import VerificationModal from './VerificationModal';
import { ComposerStep, SuccessStep, TemplateSelectionStep } from '../features/websites/builder/WebsiteBuilderSteps';
import {
  redirectToPaymentLink,
  redirectToDiscountedPaymentLink,
  savePendingWebsite,
  savePendingPromoCode,
  getPendingWebsite,
  getPendingPromoCode,
  clearPendingWebsite,
  verifyPaymentToken,
} from '../services/stripeService';

function useBuilderSensors() {
  return useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
}

function useWebsiteBuilderState(websiteId) {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [websiteTitle, setWebsiteTitle] = useState('');
  const [websiteDescription, setWebsiteDescription] = useState('');
  const [sections, setSections] = useState([]);
  const [customCSS, setCustomCSS] = useState('');
  const [customJS, setCustomJS] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(Boolean(websiteId));
  const [error, setError] = useState(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  return {
    step,
    setStep,
    selectedTemplate,
    setSelectedTemplate,
    theme,
    setTheme,
    websiteTitle,
    setWebsiteTitle,
    websiteDescription,
    setWebsiteDescription,
    sections,
    setSections,
    customCSS,
    setCustomCSS,
    customJS,
    setCustomJS,
    uploadedImages,
    setUploadedImages,
    qrCodeUrl,
    setQrCodeUrl,
    activeSection,
    setActiveSection,
    loading,
    setLoading,
    error,
    setError,
    isVerificationModalOpen,
    setIsVerificationModalOpen,
    verificationError,
    setVerificationError,
    stripeLoading,
    setStripeLoading,
    paymentProcessing,
    setPaymentProcessing,
  };
}

function applyLoadedWebsiteData(websiteData, state) {
  state.setWebsiteTitle(websiteData.title || '');
  state.setWebsiteDescription(websiteData.description || '');
  state.setSelectedTemplate(getTemplateById(websiteData.template));
  const loadedTheme = getThemeById(websiteData.theme) || DEFAULT_THEME;
  console.log('Loaded theme:', loadedTheme);
  state.setTheme(loadedTheme);
  state.setSections(websiteData.sections || []);
  state.setCustomCSS(websiteData.customCSS || '');
  state.setCustomJS(websiteData.customJS || '');
  state.setUploadedImages(websiteData.images || []);
  state.setActiveSection(websiteData.sections?.[0]?.id || null);
}

function useExistingWebsiteLoader({ websiteId, userId, state }) {
  const {
    setActiveSection,
    setCustomCSS,
    setCustomJS,
    setError,
    setLoading,
    setSections,
    setSelectedTemplate,
    setTheme,
    setUploadedImages,
    setWebsiteDescription,
    setWebsiteTitle,
  } = state;

  useEffect(() => {
    if (!websiteId) return;

    async function loadWebsiteData() {
      try {
        setLoading(true);
        const websiteDoc = await getDoc(doc(db, 'websites', websiteId));

        if (!websiteDoc.exists()) {
          setError('Уебсайт не е намерен');
          setLoading(false);
          return;
        }

        const websiteData = websiteDoc.data();
        if (websiteData.userId !== userId) {
          setError('Нямаш разрешение да променяш този уебсайт!');
          setLoading(false);
          return;
        }

        applyLoadedWebsiteData(websiteData, {
          setActiveSection,
          setCustomCSS,
          setCustomJS,
          setSections,
          setSelectedTemplate,
          setTheme,
          setUploadedImages,
          setWebsiteDescription,
          setWebsiteTitle,
        });
        setLoading(false);
      } catch (loadError) {
        setError(`Грешка при зареждане на уебсайт: ${loadError.message}`);
        setLoading(false);
      }
    }

    loadWebsiteData();
  }, [
    websiteId,
    userId,
    setActiveSection,
    setCustomCSS,
    setCustomJS,
    setError,
    setLoading,
    setSections,
    setSelectedTemplate,
    setTheme,
    setUploadedImages,
    setWebsiteDescription,
    setWebsiteTitle,
  ]);
}

function createBestMenAndMaidsDefaults(componentType) {
  if (componentType !== 'bestMenAndMaids') return {};

  return {
    title: 'Нашите Кумове',
    description: '',
    photoCount: 2,
    photos: [],
  };
}

function cloneDefaultProps(value) {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}

function useSectionActions({ sections, setSections, activeSection, setActiveSection }) {
  const updateSectionProps = (sectionId, props) => {
    setSections((previous) =>
      previous.map((section) =>
        section.id === sectionId ? { ...section, props: { ...section.props, ...props } } : section
      )
    );
  };

  const addSection = (componentType) => {
    const defaultProps = cloneDefaultProps(COMPONENTS[componentType]?.defaultProps || {});
    const newSection = {
      id: uuidv4(),
      type: componentType,
      props: { ...defaultProps, ...cloneDefaultProps(createBestMenAndMaidsDefaults(componentType)) },
      styles: {},
    };

    setSections((previous) => [...previous, newSection]);
    setActiveSection(newSection.id);
  };

  const removeSection = (sectionId) => {
    setSections((previous) => previous.filter((section) => section.id !== sectionId));
    if (activeSection === sectionId) setActiveSection(sections[0]?.id || null);
  };

  return { updateSectionProps, addSection, removeSection };
}

function useDragAndDropSections({ sections, setSections }) {
  return (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextSections = [...sections];
    const [reorderedSection] = nextSections.splice(oldIndex, 1);
    nextSections.splice(newIndex, 0, reorderedSection);
    setSections(nextSections);
  };
}

function isTimelineEventImageField(field, eventIndex) {
  return field.startsWith('event-') && field.includes('-image') && eventIndex !== null;
}

function updateTimelineEventImage({ sections, sectionId, eventIndex, imageUrl, updateSectionProps }) {
  const section = sections.find((item) => item.id === sectionId);
  if (!section?.props?.events) return;

  const events = [...section.props.events];
  if (eventIndex < 0 || eventIndex >= events.length) return;
  events[eventIndex] = { ...events[eventIndex], image: imageUrl };
  updateSectionProps(sectionId, { events });
}

function appendImageFieldValue({ sections, sectionId, field, imageUrl, updateSectionProps }) {
  const section = sections.find((item) => item.id === sectionId);
  const currentImages = Array.isArray(section?.props?.[field]) ? section.props[field] : [];
  updateSectionProps(sectionId, { [field]: [...currentImages, imageUrl] });
}

function useImageUploadHandler({ sections, setUploadedImages, updateSectionProps }) {
  return async (event, sectionId, field, eventIndex = null) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const imageUrl = await uploadImage(file);
      const uploadedImage = {
        sectionId,
        field,
        name: file.name,
        url: imageUrl,
        timestamp: Date.now(),
      };
      setUploadedImages((previous) => [...previous, uploadedImage]);

      if (isTimelineEventImageField(field, eventIndex)) {
        updateTimelineEventImage({ sections, sectionId, eventIndex, imageUrl, updateSectionProps });
        return;
      }

      if (field === 'images' || field === 'photos') {
        appendImageFieldValue({ sections, sectionId, field, imageUrl, updateSectionProps });
        return;
      }

      updateSectionProps(sectionId, { [field]: imageUrl });
    } catch (uploadError) {
      alert(`Грешка при качване на снимка: ${uploadError.message}`);
    }
  };
}

function buildWebsitePayload({ userId, selectedTemplate, theme, websiteTitle, websiteDescription, sections, customCSS, customJS, uploadedImages }) {
  return {
    userId: userId || '',
    title: websiteTitle || '',
    description: websiteDescription || '',
    template: selectedTemplate.id,
    theme: theme?.id || DEFAULT_THEME.id,
    sections: sections || [],
    customCSS: customCSS || '',
    customJS: customJS || '',
    images: uploadedImages || [],
    updatedAt: new Date(),
  };
}

function validateWebsitePayload(websiteData) {
  for (const [key, value] of Object.entries(websiteData)) {
    if (value === undefined) throw new Error(`Invalid data: ${key} is undefined`);
  }
}

function useSaveWebsiteData({ websiteId, userId, state }) {
  return async () => {
    try {
      if (!state.selectedTemplate?.id) throw new Error('Не е избран темплейт');
      if (!state.theme?.id) {
        console.warn(`Theme is undefined or missing id, defaulting to '${DEFAULT_THEME.id}'`);
        state.setTheme(DEFAULT_THEME);
      }

      const websiteData = buildWebsitePayload({
        userId,
        selectedTemplate: state.selectedTemplate,
        theme: state.theme,
        websiteTitle: state.websiteTitle,
        websiteDescription: state.websiteDescription,
        sections: state.sections,
        customCSS: state.customCSS,
        customJS: state.customJS,
        uploadedImages: state.uploadedImages,
      });
      console.log('Запазване на уебсайт с данни:', websiteData);
      validateWebsitePayload(websiteData);

      const websiteDocRef = websiteId
        ? doc(db, 'websites', websiteId)
        : await addDoc(collection(db, 'websites'), { ...websiteData, createdAt: new Date() });

      if (websiteId) await updateDoc(websiteDocRef, websiteData);

      const currentWebsiteId = websiteId || websiteDocRef.id;
      state.setQrCodeUrl(`${window.location.origin}/site/${currentWebsiteId}`);
      state.setStep(3);
    } catch (saveError) {
      console.error('Грешка при запазване на уебсайт:', saveError.message, saveError);
      state.setError(`Failed to save website: ${saveError.message}`);
      alert(`Failed to save website: ${saveError.message}`);
    }
  };
}

function useVerificationActions({ userId, saveWebsiteData, state, websiteId }) {
  const saveWebsite = () => state.setIsVerificationModalOpen(true);

  const handleGiftCodeSubmit = async (code) => {
    try {
      await verifyCode(code, userId);
      state.setIsVerificationModalOpen(false);
      state.setVerificationError('');
      await saveWebsiteData();
    } catch (verificationError) {
      state.setVerificationError('Invalid or already used verification code');
    }
  };

  const handlePromoCodeSubmit = async (code) => {
    try {
      const promoCode = await verifyPromoCode(code);
      state.setIsVerificationModalOpen(false);
      state.setVerificationError('');

      savePendingPromoCode(promoCode.id);
      savePendingWebsite({
        userId,
        selectedTemplate: state.selectedTemplate,
        theme: state.theme,
        websiteTitle: state.websiteTitle,
        websiteDescription: state.websiteDescription,
        sections: state.sections,
        customCSS: state.customCSS,
        customJS: state.customJS,
        uploadedImages: state.uploadedImages,
        websiteId,
      });
      redirectToDiscountedPaymentLink();
    } catch (err) {
      state.setVerificationError('Invalid or already used promo code');
    }
  };

  const handleStripePayment = () => {
    state.setStripeLoading(true);
    savePendingWebsite({
      userId,
      selectedTemplate: state.selectedTemplate,
      theme: state.theme,
      websiteTitle: state.websiteTitle,
      websiteDescription: state.websiteDescription,
      sections: state.sections,
      customCSS: state.customCSS,
      customJS: state.customJS,
      uploadedImages: state.uploadedImages,
      websiteId,
    });
    redirectToPaymentLink();
  };

  return { saveWebsite, handleGiftCodeSubmit, handlePromoCodeSubmit, handleStripePayment };
}

function usePaymentHandler({ setError, setPaymentProcessing, setQrCodeUrl, setStep, userId }) {
  const processedRef = useRef(false);

  useEffect(() => {
    const pending = getPendingWebsite();
    if (!pending || processedRef.current) return;

    if (!verifyPaymentToken()) {
      clearPendingWebsite();
      return;
    }

    processedRef.current = true;
    setPaymentProcessing(true);

    window.history.replaceState({}, '', window.location.pathname);

    const { payload, websiteId } = pending;

    (async () => {
      try {
        const promoCodeId = getPendingPromoCode();
        if (promoCodeId) {
          await markPromoCodeAsUsed(promoCodeId, pending.userId);
        }

        clearPendingWebsite();

        const ref = websiteId
          ? doc(db, 'websites', websiteId)
          : await addDoc(collection(db, 'websites'), { ...payload, createdAt: new Date() });

        if (websiteId) await updateDoc(ref, payload);

        const newWebsiteId = websiteId || ref.id;
        setQrCodeUrl(`${window.location.origin}/site/${newWebsiteId}`);
        setStep(3);
        setPaymentProcessing(false);
      } catch (err) {
        console.error('Грешка при запазване след плащане:', err);
        setError(`Грешка при запазване: ${err.message}`);
        setPaymentProcessing(false);
      }
    })();
  }, [setError, setPaymentProcessing, setQrCodeUrl, setStep, userId]);
}

function resetBuilderState(state) {
  state.setQrCodeUrl('');
  state.setStep(1);
  state.setSelectedTemplate(null);
  state.setTheme(DEFAULT_THEME);
  state.setSections([]);
  state.setCustomCSS('');
  state.setCustomJS('');
  state.setUploadedImages([]);
  state.setWebsiteTitle('');
  state.setWebsiteDescription('');
  state.setActiveSection(null);
}

function createSectionEditorProps(state, handlers) {
  return {
    activeSectionId: state.activeSection,
    sections: state.sections,
    componentsMap: COMPONENTS,
    onUpdateSectionProps: handlers.updateSectionProps,
    onImageUpload: handlers.handleImageUpload,
  };
}

function WebsiteBuilderLoading() {
  return (
    <div className="website-builder">
      <div className="loading-spinner">
        <div className="spinner" />
        <p>зареждане...</p>
      </div>
    </div>
  );
}

function WebsiteBuilderError({ error, onBack }) {
  return (
    <div className="website-builder">
      <div className="error-message">
        <p>{error}</p>
        <button className="button primary" onClick={onBack}>
          Назад към дашборд
        </button>
      </div>
    </div>
  );
}

function BuilderHeader({ step }) {
  return (
    <div className="builder-header">
      <h1>Създайте своя QR уебсайт</h1>
      <div className="steps-indicator">
        {[1, 2, 3].map((stepNum) => (
          <div
            key={stepNum}
            className={`step-indicator ${stepNum === step ? 'active' : ''} ${stepNum < step ? 'completed' : ''}`}
          >
            {stepNum < step ? '✓' : stepNum}
          </div>
        ))}
      </div>
    </div>
  );
}

function BuilderStepContent({ state, handlers, sensors, navigate, websiteId }) {
  const handleTemplateSelection = (template) => {
    state.setSelectedTemplate(template);
    state.setTheme(getThemeById(template.theme) || DEFAULT_THEME);
    state.setSections(createInitialContent(template.id));
  };

  if (state.step === 1) {
    return (
      <TemplateSelectionStep
        websiteTitle={state.websiteTitle}
        selectedTemplate={state.selectedTemplate}
        templateCategories={TEMPLATE_CATEGORIES}
        getTemplatesByCategory={getTemplatesByCategory}
        onTitleChange={state.setWebsiteTitle}
        onSelectTemplate={handleTemplateSelection}
        onNext={handlers.goToBuilderStep}
      />
    );
  }

  if (state.step === 2) {
    return (
      <ComposerStep
        sensors={sensors}
        onDragEnd={handlers.onDragEnd}
        sections={state.sections}
        activeSection={state.activeSection}
        setActiveSection={state.setActiveSection}
        removeSection={handlers.removeSection}
        addSection={handlers.addSection}
        componentsMap={COMPONENTS}
        sectionEditorProps={createSectionEditorProps(state, handlers)}
        theme={state.theme}
        customCSS={state.customCSS}
        websiteTitle={state.websiteTitle}
        onSave={handlers.saveWebsite}
        onBack={() => state.setStep(1)}
        onPreviewWeddingGalleryPhotoUpload={handlers.handlePreviewWeddingGalleryPhotoUpload}
        onPreviewBestMenAndMaidsPhotoUpload={handlers.handlePreviewBestMenAndMaidsPhotoUpload}
      />
    );
  }

  return (
    <SuccessStep
      qrCodeUrl={state.qrCodeUrl}
      isEditMode={Boolean(websiteId)}
      onCopyUrl={handlers.copyQrUrl}
      onBackToDashboard={() => navigate('/dashboard')}
      onCreateNew={handlers.resetBuilder}
    />
  );
}

function WebsiteBuilder({ userId }) {
  const navigate = useNavigate();
  const { websiteId } = useParams();
  const state = useWebsiteBuilderState(websiteId);
  const sensors = useBuilderSensors();

  useExistingWebsiteLoader({ websiteId, userId, state });
  usePaymentHandler({
    setError: state.setError,
    setPaymentProcessing: state.setPaymentProcessing,
    setQrCodeUrl: state.setQrCodeUrl,
    setStep: state.setStep,
    userId,
  });
  const sectionActions = useSectionActions({
    sections: state.sections,
    setSections: state.setSections,
    activeSection: state.activeSection,
    setActiveSection: state.setActiveSection,
  });
  const onDragEnd = useDragAndDropSections({ sections: state.sections, setSections: state.setSections });
  const handleImageUpload = useImageUploadHandler({
    sections: state.sections,
    setUploadedImages: state.setUploadedImages,
    updateSectionProps: sectionActions.updateSectionProps,
  });
  const saveWebsiteData = useSaveWebsiteData({ websiteId, userId, state });
  const { saveWebsite, handleGiftCodeSubmit, handlePromoCodeSubmit, handleStripePayment } = useVerificationActions({
    userId,
    saveWebsiteData,
    state,
    websiteId,
  });

  const handlers = {
    ...sectionActions,
    onDragEnd,
    handleImageUpload,
    saveWebsite,
    copyQrUrl: () => {
      navigator.clipboard.writeText(state.qrCodeUrl);
      alert('URL copied to clipboard!');
    },
    resetBuilder: () => resetBuilderState(state),
    goToBuilderStep: () => {
      if (state.selectedTemplate && state.websiteTitle) {
        state.setStep(2);
        return;
      }
      alert('Моля, изберете шаблон и въведете заглавие');
    },
    handlePreviewWeddingGalleryPhotoUpload: (section, imageUrl) => {
      const currentPhotos = Array.isArray(section.props.photos) ? section.props.photos : [];
      sectionActions.updateSectionProps(section.id, { photos: [...currentPhotos, imageUrl] });
    },
    handlePreviewBestMenAndMaidsPhotoUpload: (section, file) => {
      handleImageUpload({ target: { files: [file] } }, section.id, 'photos');
    },
  };

  if (state.paymentProcessing) return <WebsiteBuilderLoading />;
  if (state.loading) return <WebsiteBuilderLoading />;
  if (state.error) return <WebsiteBuilderError error={state.error} onBack={() => navigate('/dashboard')} />;

  return (
    <div className="website-builder">
      <BuilderHeader step={state.step} />
      <BuilderStepContent state={state} handlers={handlers} sensors={sensors} navigate={navigate} websiteId={websiteId} />
      <VerificationModal
        isOpen={state.isVerificationModalOpen}
        onClose={() => {
          state.setIsVerificationModalOpen(false);
          state.setVerificationError('');
          state.setStripeLoading(false);
        }}
        onGiftCodeSubmit={handleGiftCodeSubmit}
        onPromoCodeSubmit={handlePromoCodeSubmit}
        onStripePayment={handleStripePayment}
        error={state.verificationError}
        stripeLoading={state.stripeLoading}
        originalPrice={STRIPE_CONFIG.originalPrice}
        discountedPrice={STRIPE_CONFIG.discountedPrice}
      />
    </div>
  );
}

export default WebsiteBuilder;
